/**
 * The one break model shared by `parseRichRuns` and `htmlToStructuredText`.
 *
 * A block element (p, div, li, ul, ol, blockquote, h1-h6) that contains visible
 * text emits its text followed by a paragraph break of TWO newlines. A block
 * element with no visible text (only <br>, &nbsp;, or whitespace) emits exactly
 * ONE newline. A <br> inside a block that has text emits one newline (a soft
 * break). After assembly, whitespace-only lines become empty lines, leading and
 * trailing newlines are trimmed, and any run of newlines longer than four is
 * capped at four (bounded vertical whitespace, so a crafted document cannot
 * allocate unbounded PDF pages).
 *
 * The PDF draws (N - 1) blank lines for a run of N newlines, which yields:
 *
 * | authored structure                    | newlines | PDF blank lines |
 * | ------------------------------------- | -------- | --------------- |
 * | two normal paragraphs                 | "\n\n"   | 1               |
 * | one empty paragraph between them      | "\n\n\n" | 2               |
 * | two empty paragraphs                  | "\n\n\n\n" | 3             |
 * | three or more empty paragraphs        | "\n\n\n\n" | 3 (capped)    |
 * | <br><br> inside one paragraph         | "\n\n"   | 1               |
 */

export interface RichRun {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

/** Maximum consecutive newlines the shared model may emit. */
export const MAX_BLOCK_NEWLINES = 4;

const BLOCK_TAGS: ReadonlySet<string> = new Set([
  "p",
  "div",
  "li",
  "ul",
  "ol",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

const ENTITY_PATTERN = /&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]+);/gi;
const TAG_PATTERN = /^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9-]*)(?:\s[^>]*?)?\s*\/?\s*>$/;

function decodeEntities(text: string): string {
  return text.replace(ENTITY_PATTERN, (match, entity: string) => {
    if (entity.startsWith("#")) {
      const hexadecimal = entity[1]?.toLowerCase() === "x";
      const digits = hexadecimal ? entity.slice(2) : entity.slice(1);
      const code = Number.parseInt(digits, hexadecimal ? 16 : 10);
      if (Number.isFinite(code) && code >= 0 && code <= 0x10ffff) {
        try {
          return String.fromCodePoint(code);
        } catch {
          return match;
        }
      }
      return match;
    }
    return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
  });
}

function sameStyle(
  a: Pick<RichRun, "bold" | "italic" | "underline">,
  b: Pick<RichRun, "bold" | "italic" | "underline">,
): boolean {
  return a.bold === b.bold && a.italic === b.italic && a.underline === b.underline;
}

interface BlockFrame {
  name: string;
  hasVisibleText: boolean;
  endedWithChildBoundary: boolean;
}

/**
 * Scans sanitized or hand-written HTML into styled runs whose text already
 * carries the canonical newline model. Both public consumers derive their
 * result from this single scan, so their break counts cannot drift.
 */
export function scanRichText(html: string): RichRun[] {
  if (typeof html !== "string" || html.length === 0) return [];

  const runs: RichRun[] = [];
  const frames: BlockFrame[] = [];
  let boldDepth = 0;
  let italicDepth = 0;
  let underlineDepth = 0;
  let contentVisible = false;

  const currentStyle = (): Pick<RichRun, "bold" | "italic" | "underline"> => ({
    bold: boldDepth > 0,
    italic: italicDepth > 0,
    underline: underlineDepth > 0,
  });

  const appendRaw = (text: string): void => {
    if (!text) return;
    const style = currentStyle();
    const last = runs[runs.length - 1];
    if (last && sameStyle(last, style)) {
      last.text += text;
      return;
    }
    runs.push({ text, ...style });
  };

  const lastCharacter = (): string => {
    for (let index = runs.length - 1; index >= 0; index -= 1) {
      const text = runs[index]!.text;
      if (text.length > 0) return text[text.length - 1]!;
    }
    return "";
  };

  const atLineStart = (): boolean =>
    !contentVisible || runs.length === 0 || lastCharacter() === "\n";

  const trailingNewlines = (): number => {
    let count = 0;
    for (let index = runs.length - 1; index >= 0; index -= 1) {
      const text = runs[index]!.text;
      for (let cursor = text.length - 1; cursor >= 0; cursor -= 1) {
        if (text[cursor] !== "\n") return count;
        count += 1;
      }
    }
    return count;
  };

  const trimTrailingHorizontal = (): void => {
    for (let index = runs.length - 1; index >= 0; index -= 1) {
      const run = runs[index]!;
      run.text = run.text.replace(/[ \t\r]+$/, "");
      if (run.text.length > 0) return;
    }
  };

  const markInlineContent = (): void => {
    for (const frame of frames) frame.endedWithChildBoundary = false;
  };

  const appendNewlines = (count: number): void => {
    if (!contentVisible || count <= 0) return;
    trimTrailingHorizontal();
    const trailing = trailingNewlines();
    const allowed = Math.min(count, MAX_BLOCK_NEWLINES - trailing);
    if (allowed > 0) appendRaw("\n".repeat(allowed));
  };

  const appendParagraphBreak = (): void => {
    const trailing = trailingNewlines();
    if (trailing >= 2) return;
    appendNewlines(2 - trailing);
  };

  const appendBlankBreak = (): void => {
    appendNewlines(1);
  };

  const appendText = (raw: string): void => {
    if (!raw) return;
    let text = decodeEntities(raw).replace(/\r\n?/g, "\n");
    if (!text) return;

    const visible = /\S/.test(text);
    if (!visible) {
      if (atLineStart() || /\s$/.test(lastCharacter())) return;
      appendRaw(" ");
      markInlineContent();
      return;
    }

    if (atLineStart()) text = text.replace(/^\s+/, "");
    if (!text) return;

    appendRaw(text);
    contentVisible = true;
    for (const frame of frames) {
      frame.hasVisibleText = true;
      frame.endedWithChildBoundary = false;
    }
  };

  const closeFrame = (frame: BlockFrame): void => {
    if (frame.hasVisibleText) {
      appendParagraphBreak();
    } else if (!frame.endedWithChildBoundary) {
      appendBlankBreak();
    }
    for (const parent of frames) parent.endedWithChildBoundary = true;
  };

  const closeBlock = (name: string): void => {
    let target = -1;
    for (let index = frames.length - 1; index >= 0; index -= 1) {
      if (frames[index]!.name === name) {
        target = index;
        break;
      }
    }
    if (target === -1) return;
    while (frames.length > target) closeFrame(frames.pop()!);
  };

  const processTag = (rawTag: string): boolean => {
    const match = TAG_PATTERN.exec(rawTag);
    if (!match) return false;

    const closing = match[1] === "/";
    const name = match[2]!.toLowerCase();
    const selfClosing = /\/\s*>$/.test(rawTag);

    if (name === "br") {
      if (!closing && frames.length === 0) {
        appendNewlines(1);
        markInlineContent();
      } else if (!closing && frames[frames.length - 1]!.hasVisibleText) {
        appendNewlines(1);
        markInlineContent();
      }
      return true;
    }

    if (BLOCK_TAGS.has(name)) {
      if (closing) {
        closeBlock(name);
      } else {
        frames.push({ name, hasVisibleText: false, endedWithChildBoundary: false });
        if (selfClosing) closeBlock(name);
      }
      return true;
    }

    switch (name) {
      case "strong":
      case "b":
        boldDepth = closing ? Math.max(0, boldDepth - 1) : boldDepth + 1;
        break;
      case "em":
      case "i":
        italicDepth = closing ? Math.max(0, italicDepth - 1) : italicDepth + 1;
        break;
      case "u":
        underlineDepth = closing ? Math.max(0, underlineDepth - 1) : underlineDepth + 1;
        break;
      default:
        break;
    }
    return true;
  };

  let index = 0;
  while (index < html.length) {
    const nextTag = html.indexOf("<", index);
    if (nextTag === -1) {
      appendText(html.slice(index));
      break;
    }

    if (nextTag > index) appendText(html.slice(index, nextTag));

    if (html.startsWith("<!--", nextTag)) {
      const commentEnd = html.indexOf("-->", nextTag + 4);
      if (commentEnd === -1) break;
      index = commentEnd + 3;
      continue;
    }

    const tagEnd = html.indexOf(">", nextTag + 1);
    if (tagEnd === -1) {
      appendText(html.slice(nextTag));
      break;
    }

    const rawTag = html.slice(nextTag, tagEnd + 1);
    if (!processTag(rawTag)) {
      appendText("<");
      index = nextTag + 1;
      continue;
    }
    index = tagEnd + 1;
  }

  capNewlines(runs);
  normalizeWhitespaceOnlyLines(runs);

  while (runs.length > 0) {
    const first = runs[0]!;
    first.text = first.text.replace(/^\s+/, "");
    if (first.text.length > 0) break;
    runs.shift();
  }
  while (runs.length > 0) {
    const last = runs[runs.length - 1]!;
    last.text = last.text.replace(/\s+$/, "");
    if (last.text.length > 0) break;
    runs.pop();
  }

  return runs.filter((run) => run.text.length > 0);
}

/** Collapses any run of more than MAX_BLOCK_NEWLINES newlines across runs. */
function capNewlines(runs: RichRun[]): void {
  let consecutive = 0;
  for (const run of runs) {
    let text = "";
    for (const character of run.text) {
      if (character === "\n") {
        if (consecutive < MAX_BLOCK_NEWLINES) {
          text += character;
          consecutive += 1;
        }
        continue;
      }
      text += character;
      consecutive = 0;
    }
    run.text = text;
  }
}

/** Drops the characters of lines that contain only whitespace, keeping the line. */
function normalizeWhitespaceOnlyLines(runs: RichRun[]): void {
  const drop = runs.map((run) => new Array<boolean>(run.text.length).fill(false));
  let positions: Array<{ run: number; index: number; character: string }> = [];
  let lineHasVisibleText = false;

  const flushLine = (): void => {
    if (!lineHasVisibleText) {
      for (const position of positions) {
        if (position.character !== "\n") drop[position.run]![position.index] = true;
      }
    }
    positions = [];
    lineHasVisibleText = false;
  };

  for (let runIndex = 0; runIndex < runs.length; runIndex += 1) {
    const text = runs[runIndex]!.text;
    for (let index = 0; index < text.length; index += 1) {
      const character = text[index]!;
      if (character === "\n") {
        flushLine();
        continue;
      }
      positions.push({ run: runIndex, index, character });
      if (!/\s/.test(character)) lineHasVisibleText = true;
    }
  }
  flushLine();

  for (let runIndex = 0; runIndex < runs.length; runIndex += 1) {
    const text = runs[runIndex]!.text;
    let normalized = "";
    for (let index = 0; index < text.length; index += 1) {
      if (!drop[runIndex]![index]) normalized += text[index]!;
    }
    runs[runIndex]!.text = normalized;
  }
}

/** The concatenated text of a scan, before presentation-level whitespace cleanup. */
export function richRunsToText(runs: RichRun[]): string {
  return runs.map((run) => run.text).join("");
}
